import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Pulls the backend's `error.message` out of an RTK Query error, for
 * surfacing a specific reason (e.g. "You can't grant a role higher
 * than your own.") instead of a generic "Something went wrong" toast.
 * Every API error follows the `{ error: { code, message } }` envelope
 * from `AgencyOsWeb.FallbackController` — this reads that shape
 * defensively (RTK Query's error type is a union that also covers
 * network failures, parsing failures, etc., which won't have it) and
 * falls back to `fallback` for anything else.
 */
export function apiErrorMessage(err: unknown, fallback: string): string {
  if (
    err &&
    typeof err === "object" &&
    "data" in err &&
    err.data &&
    typeof err.data === "object" &&
    "error" in err.data &&
    err.data.error &&
    typeof err.data.error === "object" &&
    "message" in err.data.error &&
    typeof err.data.error.message === "string"
  ) {
    return err.data.error.message;
  }
  return fallback;
}

const relativeFormatter = new Intl.RelativeTimeFormat(undefined, {
  numeric: "auto",
  style: "narrow",
});

/**
 * Short relative timestamp for chat/inbox-style UI ("2m ago", "3h
 * ago", "Tue", "Jan 4"). Falls back to a short calendar date once
 * something is more than a week old, since "5 weeks ago" is less
 * scannable than a date at that distance.
 */
export function relativeTime(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absSeconds = Math.abs(seconds);

  if (absSeconds < 45) return "Just now";
  if (absSeconds < 60 * 60) {
    return relativeFormatter.format(Math.round(seconds / 60), "minute");
  }
  if (absSeconds < 60 * 60 * 24) {
    return relativeFormatter.format(Math.round(seconds / (60 * 60)), "hour");
  }
  if (absSeconds < 60 * 60 * 24 * 7) {
    return relativeFormatter.format(
      Math.round(seconds / (60 * 60 * 24)),
      "day",
    );
  }

  // More than a week old: show a calendar date instead of "N weeks ago".
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}

/** Full, precise timestamp for tooltips/titles where exact time matters. */
export function fullTimestamp(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}
